'use client'

import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback } from 'react'
import { useReducedMotion } from 'framer-motion'
import { Menu, Button, Tooltip } from 'antd'
import type { MenuProps } from 'antd'
import { useRouter, usePathname } from 'next/navigation'
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  ShoppingCartOutlined,
  MessageOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons'
import styles from './Sidebar.module.css'
import { ClientDeskRail } from './ClientDeskRail'
import {
  clientMenuForPreview,
  isTradingDeskSidebarPath,
  sidebarRenderCollapsed,
  sidebarWidthFor,
  sidebarWidthTransition,
} from '@/shared/utils/sidebar/previewSidebar'
import { sidebarToggleAction, startDeskRailVisit, useSidebarClientState } from '@/shared/providers/state/useNavigationHydration'
import { useAppSelector, useAppDispatch } from '@/shared/providers/hooks'
import {
  selectMainSidebarCollapsed,
  selectIsMobile,
  selectSidebarHiddenOnMobile,
  setIsMobile,
  setSidebarHiddenOnMobile,
} from '@/shared/providers/state/navigationSlice'
import { useTheme } from '@/shared/theme'
import {
  useAuth,
  isClientUser,
} from '@/domains/auth'

import {
  getDynamicSidebarConfig,
  isClientAudience,
  getClientSidebarConfig,
  toMenuItems as toAdminMenuItems,
  getRoutes as getAdminRoutes,
  getPathMap as getAdminPathMap,
} from '@/shared/utils/sidebar/adminSidebarManager'

// Brand is DOM-only (Agent-M mark + MECHAGORA). Never stack canvas/shine overlays.

const SIDEBAR_NAV_ID = 'sidebar-primary-nav'

function isNonEmptyItem(
  item: { key?: string; label?: string; route?: string } | null | undefined
): boolean {
  return Boolean(item && item.key && (item.label || item.route))
}

/** Parent keys for a selected leaf (antd openKeys) — preserves submenu hierarchy. */
function findParentKeys(
  items: { key: string; children?: { key: string; children?: unknown[] }[] }[],
  selected: string,
  trail: string[] = []
): string[] | null {
  for (const item of items) {
    if (item.key === selected) return trail
    if (item.children?.length) {
      const found = findParentKeys(
        item.children as typeof items,
        selected,
        [...trail, item.key]
      )
      if (found) return found
    }
  }
  return null
}

const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect

export default function Sidebar() {
  const { token, isDark } = useTheme()
  const dispatch = useAppDispatch()
  const mainSidebarCollapsed = useAppSelector(selectMainSidebarCollapsed)
  const isMobile = useAppSelector(selectIsMobile)
  const sidebarHiddenOnMobile = useAppSelector(selectSidebarHiddenOnMobile)
  const { user: authUser, isAuthenticated } = useAuth()
  const router = useRouter()
  const pathname = usePathname()
  // Client BFF session lives outside main AuthProvider (cookie / verify-client).
  // Sidebar is above ClientProtectedRoute, so hydrate client profile for chrome.
  const [clientProfile, setClientProfile] = useState<Record<string, unknown> | null>(null)

  useEffect(() => {
    if (!pathname?.startsWith('/client')) {
      setClientProfile(null)
      return
    }
    let cancelled = false
      ; (async () => {
        try {
          const res = await fetch('/api/auth/verify-client', {
            credentials: 'same-origin',
            cache: 'no-store',
          })
          if (!res.ok) return
          const data = await res.json().catch(() => null)
          if (!cancelled && data?.user) setClientProfile(data.user as Record<string, unknown>)
        } catch {
          // Shell still works with authUser / path-based client menu
        }
      })()
    return () => {
      cancelled = true
    }
  }, [pathname])

  // Prefer BFF client profile on client routes, else main auth user
  const user = (clientProfile ?? authUser) as typeof authUser

  // /trading/preview signed-out: client menu, same as a client sees on live /trading
  const previewClientMenu = clientMenuForPreview(pathname, isAuthenticated) && !clientProfile

  // SSR-safe client detection (no localStorage). Path reinforces shell on /client/*
  const isClient =
    isClientAudience(user) ||
    isClientUser(user) ||
    Boolean(clientProfile) ||
    Boolean(pathname?.startsWith('/client')) ||
    previewClientMenu

  const homeHref =
    !isAuthenticated && !clientProfile
      ? '/'
      : isClient
        ? '/client/dashboard'
        : '/dashboard'

  const reduceMotion = useReducedMotion()

  // Server render and hydration render: desk routes draw collapsed (64px) until the
  // client has read the viewport and saved setting (layout effects, before first paint).
  // Width transition stays off through the post-load step (instant grow / collapse) and
  // turns on two frames later, for the user's clicks only.
  const { clientReady, transitions } = useSidebarClientState()
  // A new route (or a fresh mount) is a new visit: a visit-only Expand/Collapse from the
  // previous desk visit no longer applies. Runs before the page's rail layout effects.
  useIsomorphicLayoutEffect(() => {
    startDeskRailVisit()
  }, [pathname])

  // Mobile drawer always uses full label width; docked rail uses collapse width.
  const railCollapsed = !isMobile && sidebarRenderCollapsed(pathname, mainSidebarCollapsed, clientReady)
  // /trading/preview docks this same sidebar with the Night chrome of /trading
  const isTradingDesk = isTradingDeskSidebarPath(pathname)
  // One width on every page and role (Elena, Oct 2): 300px expanded, 64px collapsed.
  const sidebarWidth = sidebarWidthFor(railCollapsed)

  // Workstation shell — espresso rail flush to night header; cream content stays distinct
  const chromeVars = useMemo(
    () =>
      (isTradingDesk
        ? {
            ['--sb-primary' as string]: '#3a8cff',
            ['--sb-text' as string]: '#f5f5f5',
            ['--sb-muted' as string]: '#9a9a9a',
            ['--sb-heading' as string]: '#f5f5f5',
            ['--sb-surface' as string]: '#0d0d0d',
            ['--sb-header-bg' as string]: '#161616',
            ['--sb-ring' as string]: '#3a8cff',
            ['--sb-border' as string]: '#1e1e1e',
            ['--sb-footer-bg' as string]: '#161616',
          }
        : {
            ['--sb-primary' as string]: token.colorPrimary,
            ['--sb-text' as string]: 'var(--shell-sidebar-text, #FBF6F0)',
            ['--sb-muted' as string]: '#E8D4C0',
            ['--sb-heading' as string]: '#F5E6D6',
            ['--sb-surface' as string]: '#1A1411',
            ['--sb-header-bg' as string]: 'var(--shell-sidebar-header, #2A211C)',
            ['--sb-ring' as string]: token.colorPrimary,
            ['--sb-border' as string]: 'var(--shell-sidebar-border, #3D2E28)',
            ['--sb-footer-bg' as string]: 'var(--shell-sidebar-footer, #2A211C)',
          }) as React.CSSProperties,
    [isTradingDesk, token.colorPrimary]
  )

  // Dynamic sidebar styles — shell surface tokens (not shared app container color)
  const sidebarStyles = useMemo(
    () => ({
      container: {
        backgroundColor: isTradingDesk ? '#0d0d0d' : '#1A1411',
        color: 'var(--shell-sidebar-text, #FBF6F0)',
        overflow: 'hidden' as const,
        overflowX: 'hidden' as const,
        overflowY: 'hidden' as const,
        width: sidebarWidth,
        maxWidth: isMobile ? 'min(100vw, 280px)' : sidebarWidth,
        minWidth: isMobile ? 0 : sidebarWidth,
        height: '100%',
        maxHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column' as const,
        boxSizing: 'border-box' as const,
        transition: sidebarWidthTransition(transitions, reduceMotion),
      },
      header: {
        backgroundColor: isTradingDesk
          ? '#161616'
          : 'var(--shell-sidebar-header, #2A211C)',
        borderBottom: isTradingDesk ? '1px solid #1e1e1e' : '1px solid #3D322C',
      },
      footer: {
        backgroundColor: isTradingDesk ? '#161616' : '#2A211C',
        borderTop: isTradingDesk ? '1px solid #1e1e1e' : '1px solid #3D322C',
        boxSizing: 'border-box' as const,
      },
      menu: {
        backgroundColor: 'transparent',
        border: 'none',
        overflow: 'visible' as const,
        overflowX: 'hidden' as const,
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box' as const,
      },
      nav: {
        width: '100%',
        maxWidth: '100%',
        flex: 1,
        minHeight: 0,
        boxSizing: 'border-box' as const,
        display: 'flex',
        flexDirection: 'column' as const,
        overflow: 'hidden' as const,
      },
    }),
    [sidebarWidth, isMobile, isTradingDesk, reduceMotion, transitions]
  )

  useEffect(() => {
    const checkIsMobile = () => {
      const isMobileView = window.innerWidth < 768
      dispatch(setIsMobile(isMobileView))

      // On mobile: start with sidebar hidden, auto-collapse when switching to mobile
      if (isMobileView && !sidebarHiddenOnMobile) {
        dispatch(setSidebarHiddenOnMobile(true))
      }
    }
    checkIsMobile()
    window.addEventListener('resize', checkIsMobile)
    return () => window.removeEventListener('resize', checkIsMobile)
  }, [dispatch, sidebarHiddenOnMobile])

  useEffect(() => {
    try {
      const sidebar = document.querySelector('.sidebar')
      if (!sidebar) return
      const icons = sidebar.querySelectorAll('.sidebar-menu .anticon')
      const EXTRA = 28
      const setSize = (el: Element) => {
        const rect = (el as HTMLElement).getBoundingClientRect()
        const size = Math.ceil(Math.max(rect.width, rect.height) + EXTRA)
          ; (el as HTMLElement).style.setProperty('--ring-size', `${size}px`)
      }
      icons.forEach(setSize)
      const ro = new ResizeObserver((entries) => {
        for (const entry of entries) {
          setSize(entry.target)
        }
      })
      icons.forEach((el) => ro.observe(el))
      return () => ro.disconnect()
    } catch {
      /* ignore */
    }
  }, [railCollapsed])

  const [activeIcon, setActiveIcon] = useState<HTMLElement | null>(null)

  // getDynamicSidebarConfig already returns getClientSidebarConfig() for clients
  const config = useMemo(() => {
    const dynamic = getDynamicSidebarConfig(user)
    // Path-only client shell (user not yet hydrated): still show client menu
    if (isClient && !isClientAudience(user) && (pathname?.startsWith('/client') || previewClientMenu)) {
      return getClientSidebarConfig()
    }
    return dynamic
  }, [user, isClient, pathname, previewClientMenu])

  const items = useMemo(() => {
    const next = (config.items || []).filter(isNonEmptyItem)
    if (next.length === 0 && isClient) {
      return getClientSidebarConfig().items.filter(isNonEmptyItem)
    }
    return next
  }, [config.items, isClient])

  const menuItems = toAdminMenuItems(items)
  const routes = getAdminRoutes(items)
  const pathMap = getAdminPathMap(items)

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    const route = routes[key]
    if (route) router.push(route)
  }

  const selectedKey = useMemo((): string => {
    if (pathname && pathMap[pathname]) return pathMap[pathname]
    // Longest-prefix match (client + admin nested) — do not invent new routes
    const match = Object.keys(pathMap)
      .filter((p) => p !== '/' && pathname?.startsWith(p))
      .sort((a, b) => b.length - a.length)[0]
    if (match && pathMap[match]) return pathMap[match]
    return isClient ? 'client-dashboard' : 'dashboard'
  }, [pathname, pathMap, isClient])

  const parentKeys = useMemo(
    () => findParentKeys(items, selectedKey) ?? [],
    [items, selectedKey]
  )
  const [openKeys, setOpenKeys] = useState<string[]>(parentKeys)

  // Keep parent submenu open for the active route (hierarchy continuity)
  useEffect(() => {
    if (railCollapsed) return
    setOpenKeys((prev) => Array.from(new Set([...prev, ...parentKeys])))
  }, [parentKeys, railCollapsed, selectedKey])

  const handleOpenChange = useCallback((keys: string[]) => {
    setOpenKeys(keys)
  }, [])

  /** Desktop: collapse rail. Mobile: close drawer (navbar owns open). */
  const handleCollapseToggle = useCallback(() => {
    if (isMobile) {
      dispatch(setSidebarHiddenOnMobile(true))
    } else {
      dispatch(sidebarToggleAction(pathname, window.innerWidth, railCollapsed))
    }
  }, [dispatch, isMobile, pathname, railCollapsed])

  useEffect(() => {
    const sidebar = document.querySelector('.sidebar') as HTMLElement | null
    if (!sidebar) return
    const resetAllIcons = () => {
      const allIcons = sidebar.querySelectorAll('.sidebar .anticon')
      allIcons.forEach((icon) => {
        if (icon instanceof HTMLElement) {
          icon.style.color = ''
          icon.style.filter = ''
        }
      })
    }
    const findActiveIcon = () => {
      const activeItem =
        sidebar.querySelector('.sidebar-menu .ant-menu-item-selected') ||
        sidebar.querySelector(
          '.sidebar-menu .ant-menu-submenu-selected > .ant-menu-submenu-title'
        )
      if (!activeItem) return null
      return activeItem.querySelector('.anticon') as HTMLElement | null
    }
    const updateActive = () => {
      resetAllIcons()
      const icon = findActiveIcon()
      setActiveIcon(icon)
      if (!icon) return
      // Single selected indicator: primary tint only (no glow double)
      const root = getComputedStyle(document.documentElement)
      const primaryColor =
        root.getPropertyValue('--color-primary').trim() || token.colorPrimary
      icon.style.color = primaryColor
      icon.style.filter = ''
    }
    updateActive()
    const handleUpdate = () => requestAnimationFrame(updateActive)
    window.addEventListener('resize', handleUpdate)
    const sidebarScrollEl = document.querySelector('.sidebar-scroll') as HTMLElement | null
    if (sidebarScrollEl)
      sidebarScrollEl.addEventListener('scroll', handleUpdate, { passive: true })
    return () => {
      window.removeEventListener('resize', handleUpdate)
      if (sidebarScrollEl) sidebarScrollEl.removeEventListener('scroll', handleUpdate)
    }
  }, [pathname, railCollapsed, isDark, token.colorPrimary, selectedKey])

  useEffect(() => {
    if (!activeIcon) return
    return () => {
      try {
        activeIcon.style.color = ''
        activeIcon.style.filter = ''
      } catch {
        /* ignore */
      }
    }
  }, [activeIcon])

  // Don't render sidebar if hidden on mobile
  if (isMobile && sidebarHiddenOnMobile) {
    return null
  }

  const collapseLabel = isMobile
    ? 'Close navigation'
    : railCollapsed
      ? 'Expand sidebar'
      : 'Collapse sidebar'

  return (
    <nav
      className={`sidebar sidebar-container ${styles.root} ${railCollapsed ? styles.collapsed : ''}`}
      style={{ ...sidebarStyles.container, ...chromeVars }}
      aria-label="Application"
      data-collapsed={railCollapsed ? 'true' : 'false'}
      data-rail-transition={transitions ? 'on' : 'off'}
    >
      <header
        className={`sidebar-header ${styles.header} ${railCollapsed ? styles.headerCollapsed : ''}`}
        style={sidebarStyles.header}
      >
        <div className={styles.headerInner}>
          <Tooltip
            title={railCollapsed ? 'Mechagora Trading — Dashboard' : undefined}
            placement="right"
          >
            <button
              type="button"
              className={`${styles.brand} ${railCollapsed ? styles.brandCollapsed : ''}`}
              onClick={() => router.push(homeHref)}
              aria-label="Mechagora — go to dashboard"
            >
              <img
                src="/assets/images/RobotBrand.jpg"
                className={styles.brandMark}
                alt=""
                width={28}
                height={28}
              />
              {!railCollapsed && (
                <span className={styles.brandStack}>
                  <span className={styles.brandName}>MECHAGORA</span>
                  <span className={styles.brandMeta}>Trading desk</span>
                </span>
              )}
            </button>
          </Tooltip>
          <Tooltip title={collapseLabel} placement={railCollapsed ? 'right' : 'bottom'}>
            <Button
              type="text"
              aria-label={collapseLabel}
              aria-expanded={isMobile ? true : !railCollapsed}
              aria-controls={SIDEBAR_NAV_ID}
              onClick={handleCollapseToggle}
              icon={
                isMobile || !railCollapsed ? (
                  <MenuFoldOutlined />
                ) : (
                  <MenuUnfoldOutlined />
                )
              }
              className={`sidebar-toggle ${styles.toggle} ${railCollapsed ? styles.toggleCollapsed : ''}`}
            />
          </Tooltip>
        </div>
      </header>

      <div className="sidebar-nav" style={sidebarStyles.nav}>
        {isClient ? (
          <ClientDeskRail
            items={items as import('@/shared/utils/sidebar/clientSidebarConfig').ClientSidebarItem[]}
            selectedKey={selectedKey}
            collapsed={railCollapsed}
            navId={SIDEBAR_NAV_ID}
            onNavigate={(route) => router.push(route)}
          />
        ) : (
          <div
            className={`sidebar-scroll custom-scrollbar ${styles.scroll}`}
            style={{
              height: '100%',
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box' as const,
              display: 'flex',
              flexDirection: 'column' as const,
              overflow: 'auto',
              overflowX: 'hidden',
              flex: 1,
              minHeight: 0,
            }}
          >
            <Menu
              id={SIDEBAR_NAV_ID}
              mode="inline"
              theme="dark"
              items={menuItems}
              selectedKeys={[selectedKey]}
              openKeys={railCollapsed ? [] : openKeys}
              onOpenChange={handleOpenChange}
              onClick={handleMenuClick}
              style={{
                ...sidebarStyles.menu,
                padding: '0',
                flex: 1,
                overflow: 'visible',
              }}
              inlineCollapsed={railCollapsed}
              className="sidebar-menu"
              inlineIndent={16}
              aria-label="Primary navigation"
            />
          </div>
        )}
      </div>

      {!isClient && (
      <div
        className={`sidebar-footer ${styles.footer} ${railCollapsed ? styles.footerCollapsed : ''}`}
        style={sidebarStyles.footer}
      >
        {!railCollapsed && (
          <span className={styles.dockLabel} id="sidebar-quick-actions-label">
            Shortcuts
          </span>
        )}
        <div
          className={`${styles.dock} ${railCollapsed ? styles.dockCollapsed : ''}`}
          role="group"
          aria-labelledby={railCollapsed ? undefined : 'sidebar-quick-actions-label'}
          aria-label={railCollapsed ? 'Shortcuts' : undefined}
        >
          <Tooltip title="Trade" placement={railCollapsed ? 'right' : 'top'}>
            <button
              type="button"
              className={styles.dockBtn}
              aria-label="Open trade desk"
              onClick={() => router.push('/trading')}
            >
              <ShoppingCartOutlined aria-hidden />
            </button>
          </Tooltip>
          <Tooltip title="Chat" placement={railCollapsed ? 'right' : 'top'}>
            <button
              type="button"
              className={styles.dockBtn}
              aria-label="Open desk chat"
              onClick={() => router.push('/chat-ai')}
            >
              <MessageOutlined aria-hidden />
            </button>
          </Tooltip>
          <Tooltip title="Help" placement={railCollapsed ? 'right' : 'top'}>
            <button
              type="button"
              className={styles.dockBtn}
              aria-label="Open help"
              onClick={() => router.push('/help')}
            >
              <QuestionCircleOutlined aria-hidden />
            </button>
          </Tooltip>
        </div>
      </div>
      )}
    </nav>
  )
}
